import { Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { WcGridModule } from '../../../components/wc-grid/wc-grid.module';
import { WcGridComponent } from '../../../components/wc-grid/wc-grid.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { GridApiService } from '../../../components/wc-grid/services/grid-api.service';
import { LookupOption } from '../../../components/wc-grid/grid-types';
import { TargetWellPickerComponent, TargetWellPickerData } from './target-well-picker.component';

/**
 * Manages LegacyXRef well mappings for one DataFeed (Direct Exchange / legacy). Backed by the
 * "legacyXRef" grid (see LegacyXRefGridSource.cs on the server):
 *  - Enable/disable a row and manually type a Target Id: plain inline-editable grid columns,
 *    staged and saved together via the grid's normal multi-row Save (bulk edit).
 *  - "Map (Assisted)": a row context-menu action that opens a searchable picker of the target
 *    client's available wells (sec.IX_Wells) and immediately applies the selected well's
 *    Id_Source as that row's Target Id.
 */
@Component({
  selector: 'app-legacy-xref-editor',
  standalone: true,
  templateUrl: './legacy-xref-editor.component.html',
  styleUrls: ['./legacy-xref-editor.component.css'],
  imports: [CommonModule, WcGridModule, MatDialogModule],
})
export class LegacyXrefEditorComponent implements OnChanges {
  @Input() dataFeedId!: string;

  @ViewChild(WcGridComponent) grid?: WcGridComponent;

  gridContext: Record<string, any> = {};
  readonly gridKey = 'legacyXRef';
  readonly rowActions = [{ id: 'mapAssisted', label: 'Map (Assisted)...' }];

  constructor(private dialog: MatDialog, private gridApi: GridApiService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataFeedId'] && this.dataFeedId) {
      this.gridContext = { dataFeedId: this.dataFeedId };
    }
  }

  onRowAction(event: { id: string; row: Record<string, any> }): void {
    if (event.id !== 'mapAssisted') return;

    const ref = this.dialog.open<TargetWellPickerComponent, TargetWellPickerData, LookupOption | undefined>(
      TargetWellPickerComponent,
      {
        data: {
          dataFeedId: this.dataFeedId,
          sourceId: event.row['SourceId'],
          sourceWellName: event.row['SourceWellName'],
          sourceApi: event.row['SourceApi'],
        },
        minWidth: '480px',
      }
    );

    ref.afterClosed().subscribe(selected => {
      if (!selected) return;
      this.gridApi.bulkOps(
        this.gridKey,
        { ops: [{ rowId: event.row['Id'], op: 'Update', patch: { TargetId: selected.value } }], context: this.gridContext },
        () => this.grid?.reload(this.grid!.page),
        () => { /* the grid's own next data/save call will surface any error state */ }
      );
    });
  }
}
